import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '@/types/api';
import {
  Document,
  Packer,
  PageBreak,
  Table,
  TableCell,
  TableRow,
  Paragraph,
  TextRun,
  ImageRun,
  ExternalHyperlink,
} from 'docx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import type { Site, Photo } from '../types';
import { TableCellWithText } from '../helpers/TableCell';

export interface ExportSiteData {
  data: Site;
  materials: Array<{
    quantity: number;
    materialCode: string;
    unitType: string;
    longDescription: string;
    unitCost: number;
    materialCost: number;
    siteName: string;
  }>;
  images: Array<
    Photo & {
      photoData: string;
      width?: number;
      height?: number;
      orientation?: number;
    }
  >;
}

export interface ExportProject {
  id: number;
  projectName: string;
  discount: number;
}

export const useWordExport = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const currencyFormat = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const exportToWord = async (
    allSites: ExportSiteData[],
    project: ExportProject,
    fileGenerated: () => void
  ) => {
    try {
      // Process sites sequentially to handle async image conversion
      const paragraphs: Paragraph[] = [];

      for (const site of allSites) {
        let total = 0;

        site.materials.forEach(item => {
          total += item.materialCost || 0;
        });

        // Round total to 2 decimal places
        total = parseFloat(total.toFixed(2));

        const totalDiscount = parseFloat(
          (total * (project.discount / 100)).toFixed(2)
        );

        const siteInfo = [
          new Paragraph({
            bidirectional: isRTL,
            children: [
              new TextRun({
                text: t('site_name_exported_file', 'Site Name'),
                bold: true,
                underline: { type: 'single' },
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
              new TextRun({
                text: ` ${site.data.siteName}`,
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
            ],
          }),
          new Paragraph({
            bidirectional: isRTL,
            children: [
              new TextRun({
                text: t('latitude_and_longitude_file', 'Location'),
                bold: true,
                underline: { type: 'single' },
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
              new ExternalHyperlink({
                link: `https://www.google.com/maps?q=${site.data.latitude},${site.data.longitude}`,
                children: [
                  new TextRun({
                    text: ` ${site.data.latitude}, ${site.data.longitude}`,
                    style: 'Hyperlink',
                    size: 24,
                    underline: { type: 'none' },
                    rightToLeft: isRTL,
                    font: 'Arial',
                    language: { value: isRTL ? 'he-IL' : 'en' },
                  }),
                ],
              }),
            ],
          }),
          new Paragraph({
            bidirectional: isRTL,
            children: [
              new TextRun({
                text: t('priority_exported_file', 'Priority'),
                bold: true,
                underline: { type: 'single' },
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
              new TextRun({
                text: ` ${site.data.priority || 1}`,
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
            ],
          }),
          new Paragraph({
            bidirectional: isRTL,
            children: [
              new TextRun({
                text: t('current_state_exported_file', 'Current State'),
                bold: true,
                underline: { type: 'single' },
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
              new TextRun({
                text: ` ${t(site.data.currentState) || site.data.currentState}`,
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
            ],
          }),
          new Paragraph({
            bidirectional: isRTL,
            children: [
              new TextRun({
                text: t('required_changes_exported_file', 'Required Changes'),
                bold: true,
                underline: { type: 'single' },
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
              new TextRun({
                text: ` ${site.data.requiredChanges || ''}`,
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
            ],
          }),
          new Paragraph({
            bidirectional: isRTL,
            children: [
              new TextRun({
                text: t('description_exported_file', 'Description'),
                bold: true,
                underline: { type: 'single' },
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
              new TextRun({
                text: ` ${site.data.description || ''}`,
                size: 24,
                rightToLeft: isRTL,
                font: 'Arial',
                language: { value: isRTL ? 'he-IL' : 'en' },
              }),
            ],
          }),
        ];

        console.log('site.materials', site.materials);
        const tableRows = site.materials
          .filter(item => item.quantity > 0)
          .map(
            material =>
              new TableRow({
                children: [
                  TableCellWithText(
                    currencyFormat(material.materialCost),
                    isRTL
                  ),
                  TableCellWithText(currencyFormat(material.unitCost), isRTL),
                  TableCellWithText(material.quantity.toString(), isRTL),
                  TableCellWithText(material.unitType, isRTL),
                  TableCellWithText(material.longDescription, isRTL),
                  TableCellWithText(material.materialCode, isRTL),
                ],
              })
          );

        const additionalRows = [
          new TableRow({
            children: [
              TableCellWithText(currencyFormat(total), isRTL),
              TableCellWithText('', isRTL),
              new TableCell({
                children: [
                  new Paragraph({
                    bidirectional: isRTL,
                    children: [
                      new TextRun({
                        text: t('total_table_exported_file', 'Total'),
                        rightToLeft: isRTL,
                        language: { value: isRTL ? 'he-IL' : 'en' },
                        font: 'Arial',
                      }),
                    ],
                  }),
                ],
                columnSpan: 2,
              }),
              new TableCell({
                children: [],
                borders: {
                  top: { style: 'single', size: 1, color: 'FFFFFF' },
                  bottom: { style: 'single', size: 1, color: 'FFFFFF' },
                  left: { style: 'single', size: 1, color: 'FFFFFF' },
                  right: { style: 'single', size: 1, color: 'FFFFFF' },
                },
              }),
              new TableCell({
                children: [],
                borders: {
                  top: { style: 'single', size: 1, color: 'FFFFFF' },
                  bottom: { style: 'single', size: 1, color: 'FFFFFF' },
                  left: { style: 'single', size: 1, color: 'FFFFFF' },
                  right: { style: 'single', size: 1, color: 'FFFFFF' },
                },
              }),
            ],
          }),
          new TableRow({
            children: [
              TableCellWithText(currencyFormat(totalDiscount), isRTL),
              TableCellWithText('', isRTL),
              TableCellWithText(project.discount.toString(), isRTL),
              TableCellWithText(
                t('discount_table_exported_file', 'Discount'),
                isRTL
              ),
              new TableCell({
                children: [],
                borders: {
                  top: { style: 'single', size: 1, color: 'FFFFFF' },
                  bottom: { style: 'single', size: 1, color: 'FFFFFF' },
                  left: { style: 'single', size: 1, color: 'FFFFFF' },
                  right: { style: 'single', size: 1, color: 'FFFFFF' },
                },
              }),
              new TableCell({
                children: [],
                borders: {
                  top: { style: 'single', size: 1, color: 'FFFFFF' },
                  bottom: { style: 'single', size: 1, color: 'FFFFFF' },
                  left: { style: 'single', size: 1, color: 'FFFFFF' },
                  right: { style: 'single', size: 1, color: 'FFFFFF' },
                },
              }),
            ],
          }),
          new TableRow({
            children: [
              TableCellWithText(currencyFormat(total - totalDiscount), isRTL),
              TableCellWithText('', isRTL),
              new TableCell({
                children: [
                  new Paragraph({
                    bidirectional: isRTL,
                    children: [
                      new TextRun({
                        text: t(
                          'total_after_discount_table_exported_file',
                          'Total After Discount'
                        ),
                        rightToLeft: isRTL,
                        language: { value: isRTL ? 'he-IL' : 'en' },
                        font: 'Arial',
                      }),
                    ],
                  }),
                ],
                columnSpan: 2,
              }),
              new TableCell({
                children: [],
                borders: {
                  top: { style: 'single', size: 1, color: 'FFFFFF' },
                  bottom: { style: 'single', size: 1, color: 'FFFFFF' },
                  left: { style: 'single', size: 1, color: 'FFFFFF' },
                  right: { style: 'single', size: 1, color: 'FFFFFF' },
                },
              }),
              new TableCell({
                children: [],
                borders: {
                  top: { style: 'single', size: 1, color: 'FFFFFF' },
                  bottom: { style: 'single', size: 1, color: 'FFFFFF' },
                  left: { style: 'single', size: 1, color: 'FFFFFF' },
                  right: { style: 'single', size: 1, color: 'FFFFFF' },
                },
              }),
            ],
          }),
        ];

        const table = new Table({
          rows: [
            new TableRow({
              children: [
                TableCellWithText(
                  t('material_cost_table_exported_file', 'Material Cost'),
                  isRTL
                ),
                TableCellWithText(
                  t('unit_cost_table_exported_file', 'Unit Cost'),
                  isRTL
                ),
                TableCellWithText(
                  t('quantity_table_exported_file', 'Quantity'),
                  isRTL
                ),
                TableCellWithText(
                  t('unit_type_table_exported_file', 'Unit Type'),
                  isRTL
                ),
                TableCellWithText(
                  t('long_description_table_exported_file', 'Description'),
                  isRTL,
                  true
                ),
                TableCellWithText(
                  t('material_code_table_exported_file', 'Material Code'),
                  isRTL
                ),
              ],
            }),
            ...tableRows,
            ...additionalRows,
          ],
        });

        // Image processing with error handling and type detection
        const imageParagraph: Paragraph[] = [];
        const imagesPerRow = 2;
        let rowImages: (TextRun | ImageRun)[] = [];

        /**
         * Converts WebP and other formats to JPEG for Word compatibility
         * Returns a promise with JPEG base64 data and original dimensions
         */
        const convertToJpeg = async (
          base64: string
        ): Promise<{ base64: string; width: number; height: number }> => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  reject(new Error('Failed to get canvas context'));
                  return;
                }
                ctx.drawImage(img, 0, 0);
                // Convert to JPEG with 90% quality
                const jpegBase64 = canvas.toDataURL('image/jpeg', 0.9);
                resolve({
                  base64: jpegBase64,
                  width: canvas.width,
                  height: canvas.height,
                });
              } catch (error) {
                reject(error);
              }
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = base64;
          });
        };

        /**
         * Detects image type from base64 data URL or signature
         */
        const detectImageType = (base64: string): string | null => {
          if (base64.includes(',')) {
            const prefix = base64.split(',')[0];
            if (prefix.includes('image/png')) return 'png';
            if (prefix.includes('image/gif')) return 'gif';
            if (prefix.includes('image/jpeg') || prefix.includes('image/jpg'))
              return 'jpg';
            if (prefix.includes('image/webp')) return 'webp';
          }

          // If no prefix, try to detect from base64 signature
          const cleanBase64 = base64.substring(0, 8);
          if (cleanBase64.startsWith('iVBOR')) return 'png';
          if (cleanBase64.startsWith('R0lGO')) return 'gif';
          if (cleanBase64.startsWith('UklGR')) return 'webp'; // RIFF header for WebP
          if (cleanBase64.startsWith('/9j/')) return 'jpg'; // JPEG signature
          return null;
        };

        const base64ToArrayBuffer = (base64: string): Uint8Array | null => {
          try {
            // Remove data URL prefix if present (e.g., "data:image/webp;base64,", "data:image/jpeg;base64,", etc.)
            const cleanBase64 = base64.includes(',')
              ? base64.split(',')[1]
              : base64;

            // Validate base64 string
            if (!cleanBase64 || cleanBase64.trim() === '') {
              console.warn('Empty base64 string');
              return null;
            }

            const binaryString = window.atob(cleanBase64);
            const len = binaryString.length;

            // Check if the decoded data is valid
            if (len === 0) {
              console.warn('Invalid base64 data - zero length after decoding');
              return null;
            }

            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            return bytes;
          } catch (error) {
            console.error('Error converting base64 to ArrayBuffer:', error);
            return null;
          }
        };

        const getImageTransformation = (
          orientation: number,
          width: number,
          height: number
        ) => {
          switch (orientation) {
            case 6:
              return { width: height, height: width, rotation: 90 };
            case 3:
              return { width, height, rotation: 180 };
            case 8:
              return { width: height, height: width, rotation: 270 };
            case 1:
            default:
              return { width, height, rotation: 0 };
          }
        };

        if (site.images && site.images.length > 0) {
          try {
            for (let index = 0; index < site.images.length; index++) {
              const image = site.images[index];
              try {
                // Validate image data before processing
                if (!image.photoData || image.photoData.trim() === '') {
                  console.warn(`Image ${index} has empty photoData, skipping`);
                  continue;
                }

                // Detect image type
                const detectedType = detectImageType(image.photoData);
                console.log(`Image ${index} detected type:`, detectedType);

                // Convert WebP to JPEG for Word compatibility
                let processedImageData = image.photoData;
                let finalImageType: 'jpg' | 'png' | 'gif' = 'jpg';
                let imageWidth = image.width;
                let imageHeight = image.height;

                if (detectedType === 'webp') {
                  console.log(`Converting WebP image ${index} to JPEG...`);
                  try {
                    const converted = await convertToJpeg(image.photoData);
                    processedImageData = converted.base64;
                    imageWidth = converted.width;
                    imageHeight = converted.height;
                    finalImageType = 'jpg';
                    console.log(
                      `Image ${index} successfully converted to JPEG (${imageWidth}x${imageHeight})`
                    );
                  } catch (conversionError) {
                    console.error(
                      `Failed to convert WebP image ${index}:`,
                      conversionError
                    );
                    continue; // Skip this image if conversion fails
                  }
                } else if (detectedType === 'png') {
                  finalImageType = 'png';
                } else if (detectedType === 'gif') {
                  finalImageType = 'gif';
                } else if (detectedType === 'jpg') {
                  finalImageType = 'jpg';
                } else {
                  console.warn(
                    `Unknown image type for image ${index}, trying as JPEG`
                  );
                  finalImageType = 'jpg';
                }

                // Calculate scaled dimensions preserving aspect ratio
                const maxWidth = 270;
                let scaledWidth = maxWidth;
                let scaledHeight = maxWidth;

                if (imageWidth && imageHeight) {
                  const aspectRatio = imageWidth / imageHeight;
                  scaledWidth = maxWidth;
                  scaledHeight = Math.round(maxWidth / aspectRatio);

                  // If height is too large, scale by height instead
                  const maxHeight = 360;
                  if (scaledHeight > maxHeight) {
                    scaledHeight = maxHeight;
                    scaledWidth = Math.round(maxHeight * aspectRatio);
                  }

                  console.log(
                    `Image ${index} scaled from ${imageWidth}x${imageHeight} to ${scaledWidth}x${scaledHeight}`
                  );
                }

                const imageBuffer = base64ToArrayBuffer(processedImageData);

                // Skip image if conversion failed
                if (!imageBuffer) {
                  console.warn(
                    `Failed to convert image ${index} to buffer, skipping`
                  );
                  continue;
                }

                if (index < 1) {
                  imageParagraph.push(
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: '.'.repeat(150),
                          color: 'ffFFFF',
                          font: 'Arial',
                          size: 20,
                        }),
                      ],
                      bidirectional: isRTL,
                    })
                  );
                  rowImages = [];
                }

                rowImages.push(
                  new TextRun({
                    text: '.'.repeat(2),
                    color: 'ffFFFF',
                    font: 'Arial',
                    size: 20,
                  })
                );

                rowImages.push(
                  new ImageRun({
                    type: finalImageType,
                    data: imageBuffer,
                    transformation: getImageTransformation(
                      image.orientation || 1,
                      scaledWidth,
                      scaledHeight
                    ),
                  })
                );

                rowImages.push(
                  new TextRun({
                    text: '.'.repeat(2),
                    color: 'ffFFFF',
                    font: 'Arial',
                    size: 20,
                  })
                );

                if (index === 1 || index === 3) {
                  rowImages.push(
                    new TextRun({
                      text: '.'.repeat(150),
                      color: 'ffFFFF',
                      font: 'Arial',
                      size: 20,
                    })
                  );
                }

                if (
                  rowImages.length == imagesPerRow * 3 + 1 ||
                  index === site.images.length - 1
                ) {
                  imageParagraph.push(
                    new Paragraph({
                      children: rowImages,
                      bidirectional: isRTL,
                    })
                  );
                  rowImages = [];
                }
              } catch (imageError) {
                console.error(`Error processing image ${index}:`, imageError);
                // Continue with next image
              }
            }
          } catch (error) {
            console.error('Error processing images:', error);
            getErrorMessage(error, true);
          }
        }

        paragraphs.push(...siteInfo);
        paragraphs.push(new Paragraph({ children: [] }));
        paragraphs.push(table as any); // Table is compatible with Document children
        paragraphs.push(...imageParagraph);
        paragraphs.push(new Paragraph({ children: [new PageBreak()] }));
      }

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: paragraphs,
          },
        ],
      });

      await Packer.toBlob(doc).then(blob => {
        const currentDate = format(new Date(), 'yyyy-MM-dd_HH-mm');
        saveAs(blob, `${project.projectName}_${currentDate}.docx`);
        fileGenerated();
        return true;
      });
    } catch (error) {
      getErrorMessage(error, true);
      fileGenerated();
      throw error;
    }
  };

  return { exportToWord };
};
